// src/pages/ProgramBuilder.js
// ============================================================================
// Program Builder — Milestone 1 shell + Milestone 2a persistence.
//
// Consumes the PROVEN foundation, unmodified:
//   useProgramAssets()  -> { programClips, signedUrlMap, loading }
//   programClipToEntry(asset, order) -> frozen 7-field ProgramEntry (throws on bad clip)
//   normalizeOrder(entries) -> re-sequences order 0,1,2,...
//
// M1 SCOPE (locked):
//   Library    : real useProgramAssets, search-by-name, cards = sprite+name+duration+Add.
//   Composition: real state via programClipToEntry + normalizeOrder. Cards = order+sprite+
//                name+duration+remove+↑/↓. Duplicates allowed. Real totals. Empty state.
//   Settings   : Program Name = working local state. Summary = REAL derived values.
//                Accent / Player Preview / Folder / Tags = OBVIOUS inactive regions
//                (no fake controls, no fake data).
//   Header     : Cancel WORKS (-> /programs). Save Program persists to Firestore
//                (M2a): create via addDoc, edit via updateDoc on /programs/:id/edit.
//
// OUT OF SCOPE (do not add): folder data/CRUD/UI, tags UI, accent/theme UI, real
//   Player preview, drag/drop, undo/redo, dirty-nav interception, autosave, and
//   swapping the Programs page to Firestore data (prove Builder↔Firestore↔Builder
//   in isolation first). Persistence + /programs/:id/edit route ARE in scope for
//   M2a. folderId/tags are round-tripped losslessly but NOT edited here. Do NOT
//   modify the proven pieces.
//
// Layout uses inline styles for PROPORTIONS ONLY (structure-then-style); visual
// skinning to Pulse language is a later pass.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, setDoc, onSnapshot, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { useProgramAssets } from '../hooks/useAssets';
import { useFolders } from '../hooks/useFolders';
import { tagColor, tagBg } from '../utils/tagUtils';
import TagEditor from '../components/TagEditor';
import CoverImageEditor from '../components/CoverImageEditor';
import { Plus, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { programClipToEntry, normalizeOrder } from '../builder/programClipToEntry';

const mmss = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// Locked schema constants (M2a). Accent control is not wired yet, so persist the
// locked default theme (the renderer's live CSS-var defaults). folderId/tags are
// honestly empty (unfiled/untagged) — the folder system is a separate track.
const SCHEMA_VERSION = 1;
const DEFAULT_THEME = { accent: '#c8e020', text: '#ffffff', muted: '#8a8f98' };

// Prescription text describing what's BAKED into the clip (matches ExerciseBuilder's rxText).
// Shown as a selection aid so users can tell reps-clips from duration-clips at a glance.
function prescriptionText(cfg) {
  if (!cfg) return '';
  const sets = cfg.sets;
  if (cfg.rxMode === 'reps') {
    return (sets && cfg.reps) ? `${sets} × ${cfg.reps} reps` : '';
  }
  if (cfg.rxMode === 'duration') {
    const dur = cfg.duration ?? cfg.details?.[0]?.duration ?? cfg.details?.duration;
    return (sets && dur) ? `${sets} × ${dur} sec` : '';
  }
  return '';
}
// category pill color by name (stable, subtle)
const CAT_COLORS = { lower: '#3b82f6', upper: '#8b5cf6', shoulder: '#06b6d4', core: '#10b981', 'full body': '#f59e0b' };
function catColor(name) { return CAT_COLORS[(name || '').toLowerCase()] || '#6b7280'; }
// Display-only: capitalize the first letter of each word so clip names read consistently
// (e.g. 'right arm flexion' -> 'Right Arm Flexion'). Preserves existing uppercase like
// abbreviations (KB, RDL, ER). Does NOT change stored data or the baked clip.
function titleCase(s) { return (s || '').replace(/\b\w/g, (c) => c.toUpperCase()); }

// Rebuild the 7-field ProgramEntry[] from a persisted composition, preserving
// entryId/order/snapshots exactly. Re-numbers order defensively via normalizeOrder.
const hydrateComposition = (saved) => {
  if (!Array.isArray(saved)) return [];
  const entries = saved.map((e) => ({
    entryId: e.entryId,
    order: e.order,
    assetId: e.assetId,
    key: e.key,
    exerciseName: e.exerciseName,
    durationMs: e.durationMs,
    spriteUrl: e.spriteUrl,
  }));
  return normalizeOrder(entries);
};

export default function ProgramBuilder() {
  const navigate = useNavigate();
  const { id: routeProgramId } = useParams();   // present on /programs/:id/edit
  const isEditMode = Boolean(routeProgramId);
  const { currentUser } = useAuth();
  const { programClips, loading } = useProgramAssets();

  const [programName, setProgramName] = useState('');
  const [search, setSearch] = useState('');
  const [composition, setComposition] = useState([]); // ProgramEntry[]
  const [addError, setAddError] = useState(null);

  // Persistence state
  const [programId, setProgramId] = useState(routeProgramId || null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [accentPalette, setAccentPalette] = useState([]);   // user's saved accents (users/{uid})
  const [lastAccent, setLastAccent] = useState(DEFAULT_THEME.accent);
  const [accentModalOpen, setAccentModalOpen] = useState(false);
  const [folderId, setFolderId] = useState(null);      // round-tripped losslessly; not edited in M2a
  const [tags, setTags] = useState([]);
  // cover image (staged locally; persisted on Save Program, like everything else)
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [coverZoom, setCoverZoom] = useState(1.4);
  const [coverPosX, setCoverPosX] = useState(50);
  const [coverPosY, setCoverPosY] = useState(50);
  const [coverModalOpen, setCoverModalOpen] = useState(false);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [loadingProgram, setLoadingProgram] = useState(isEditMode);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);  // brief '✓ Saved' confirmation
  const [saveError, setSaveError] = useState(null);    // persistence errors (distinct from clip addError)
  const [dirty, setDirty] = useState(false);
  const { folders } = useFolders();

  // user's saved accent palette + last-used accent (users/{uid}); persists cross-device
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setAccentPalette(Array.isArray(d.accentPalette) ? d.accentPalette : []);
        if (d.lastAccent) setLastAccent(d.lastAccent);
      }
    });
    return unsub;
  }, [currentUser?.uid]);

  // NEW programs default their accent to the user's last-used accent (settle-on-one).
  useEffect(() => {
    if (isEditMode) return;                    // editing keeps the program's saved accent
    if (theme.accent !== DEFAULT_THEME.accent) return;   // user already picked one this session
    if (lastAccent && lastAccent !== DEFAULT_THEME.accent) {
      setTheme(t => ({ ...t, accent: lastAccent }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAccent, isEditMode]);
  const [tagPool, setTagPool] = useState([]);   // existing tags across user's programs (picker suggestions)
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const qy = query(collection(db, 'programs'), where('userId', '==', currentUser.uid));
        const snap = await getDocs(qy);
        const all = new Set();
        snap.docs.forEach(d => (Array.isArray(d.data().tags) ? d.data().tags : []).forEach(t => all.add(t)));
        setTagPool(Array.from(all).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
      } catch (e) { console.error('tag pool fetch:', e); }
    })();
  }, [currentUser]);

  // --- EDIT MODE: load the existing program once (after auth is resolved) ---
  useEffect(() => {
    if (!isEditMode) return;
    if (!currentUser) return; // wait until auth resolves before fetching/ownership-checking
    let cancelled = false;
    (async () => {
      setLoadingProgram(true);
      setLoadError(null);
      try {
        const snap = await getDoc(doc(db, 'programs', routeProgramId));
        if (cancelled) return;
        if (!snap.exists()) {
          setLoadError('Program not found.');
          return;
        }
        const data = snap.data();
        // Ownership enforced at the Builder level (not only via Firestore Rules).
        // Reject a non-owned doc as "not found" so we don't disclose the ID exists.
        if (data.userId !== currentUser.uid) {
          setLoadError('Program not found.');
          return;
        }
        setProgramName(data.name || '');
        setComposition(hydrateComposition(data.composition));
        setTheme(data.theme || DEFAULT_THEME);
        setFolderId(data.folderId ?? null);                      // round-trip unchanged
        setTags(Array.isArray(data.tags) ? data.tags : []);      // round-trip unchanged
        setCoverImageUrl(data.coverImageUrl ?? null);
        setCoverZoom(data.coverZoom ?? 1.4);
        setCoverPosX(data.coverPosX ?? 50);
        setCoverPosY(data.coverPosY ?? 50);
        setProgramId(routeProgramId);
        setDirty(false); // freshly loaded == clean
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load program.');
      } finally {
        if (!cancelled) setLoadingProgram(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, routeProgramId, currentUser]);

  // --- derived (single source for both center header and right Summary) ---
  const clipCount = composition.length;
  const totalDurationMs = useMemo(
    () => composition.reduce((sum, e) => sum + (e.durationMs || 0), 0),
    [composition]
  );

  const filteredClips = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programClips;
    return programClips.filter((a) =>
      (a.exerciseConfig?.exerciseName || '').toLowerCase().includes(q)
    );
  }, [programClips, search]);

  // --- composition ops (identity-safe; renumber via normalizeOrder) ---
  const addClip = (asset) => {
    setAddError(null);
    try {
      // Validate + build the entry OUTSIDE the state updater so a validation
      // throw is caught here (updaters may run during render / more than once,
      // so they must stay pure and must not throw). Order is provisional (0);
      // normalizeOrder re-sequences on append, so the real order is derived
      // atomically from the current `prev` — rapid/batched Adds can't collide.
      const entry = programClipToEntry(asset, 0);
      setComposition((prev) => normalizeOrder([...prev, entry]));
      setDirty(true);
    } catch (e) {
      setAddError(e.message); // visible contract failure; nothing added
    }
  };

  const removeEntry = (entryId) => {
    setComposition((prev) => normalizeOrder(prev.filter((e) => e.entryId !== entryId)));
    setDirty(true);
  };

  const moveEntry = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= composition.length) return; // boundary no-op: don't mark dirty
    setComposition((prev) => {
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return normalizeOrder(next);
    });
    setDirty(true);
  };

  const onFolderChange = (v) => { setFolderId(v || null); setDirty(true); };
  const onTagsChange = (next) => { setTags(next); setDirty(true); };
  // select an accent -> stage on theme + mark dirty + remember as lastAccent (user doc)
  const selectAccent = async (hex) => {
    setTheme(t => ({ ...t, accent: hex }));
    setDirty(true);
    setLastAccent(hex);
    if (currentUser?.uid) {
      try { await setDoc(doc(db, 'users', currentUser.uid), { lastAccent: hex }, { merge: true }); }
      catch (e) { console.error('save lastAccent:', e); }
    }
  };
  // save a custom color -> add to palette (cap 6, newest first, dedupe) + select it
  const saveCustomAccent = async (hex) => {
    const lower = (hex || '').toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(lower)) return;   // valid hex only (runtime-bound)
    const next = [lower, ...accentPalette.filter(c => c.toLowerCase() !== lower)].slice(0, 6);
    setAccentPalette(next);
    if (currentUser?.uid) {
      try { await setDoc(doc(db, 'users', currentUser.uid), { accentPalette: next, lastAccent: lower }, { merge: true }); }
      catch (e) { console.error('save accentPalette:', e); }
    }
    await selectAccent(lower);
    setAccentModalOpen(false);
  };

  const onCoverChange = (c) => {
    setCoverImageUrl(c.coverImageUrl); setCoverZoom(c.coverZoom);
    setCoverPosX(c.coverPosX); setCoverPosY(c.coverPosY); setDirty(true);
  };

  const onNameChange = (v) => {
    setProgramName(v);
    setDirty(true);
  };

  // --- SAVE (explicit; create via addDoc, edit via updateDoc) ---
  const canSave = !saving && programName.trim().length > 0 && composition.length > 0;

  const save = async () => {
    if (!canSave || !currentUser) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Recompute totalDurationMs from composition on every save (never trust stale doc).
      const total = composition.reduce((sum, e) => sum + (e.durationMs || 0), 0);
      // Persist the exact 7-field entries (defensive: strip any stray fields).
      const persistComposition = composition.map((e) => ({
        entryId: e.entryId,
        order: e.order,
        assetId: e.assetId,
        key: e.key,
        exerciseName: e.exerciseName,
        durationMs: e.durationMs,
        spriteUrl: e.spriteUrl,
      }));

      if (programId) {
        // EDIT existing. folderId/tags carry the hydrated values (round-tripped
        // losslessly); theme likewise. This save does not touch fields it doesn't own.
        await updateDoc(doc(db, 'programs', programId), {
          name: programName.trim(),
          schemaVersion: SCHEMA_VERSION,
          totalDurationMs: total,
          theme,
          folderId,
          tags,
          coverImageUrl,
          coverZoom,
          coverPosX,
          coverPosY,
          composition: persistComposition,
          updatedAt: serverTimestamp(),
        });
      } else {
        // CREATE new
        const ref = await addDoc(collection(db, 'programs'), {
          name: programName.trim(),
          userId: currentUser.uid,           // ownership
          schemaVersion: SCHEMA_VERSION,
          totalDurationMs: total,
          theme,
          folderId,                          // null for a new program
          tags,                              // [] for a new program
          coverImageUrl,
          coverZoom,
          coverPosX,
          coverPosY,
          composition: persistComposition,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setProgramId(ref.id);
        // Reflect the new id in the URL so subsequent saves are edits, and a
        // refresh reloads this program. replace() avoids a back-button to /new.
        navigate(`/programs/${ref.id}/edit`, { replace: true });
      }
      setDirty(false);
      // Brief visible confirmation (create + edit). Auto-clears.
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setSaveError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // --- inline layout styles (PROPORTIONS ONLY; skin later) ---
  const S = {
    page: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100vh', background: '#f4f5f7' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#fff', borderBottom: '1px solid #e3e5e8' },
    headerTitle: { fontSize: 20, fontWeight: 700 },
    headerSub: { fontSize: 13, color: '#666', marginTop: 2 },
    headerBtns: { display: 'flex', gap: 8 },
    btn: { padding: '8px 16px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 14 },
    btnDisabled: { padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#eee', color: '#999', cursor: 'not-allowed', fontSize: 14 },
    btnPrimary: { padding: '8px 16px', borderRadius: 6, border: '1px solid #1f7a3d', background: '#22a355', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
    columns: { display: 'flex', gap: 16, padding: 16, flex: 1, alignItems: 'stretch', minHeight: 0 },
    colLibrary: { flex: '0 0 27%', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e3e5e8', borderRadius: 8, padding: 16, overflow: 'hidden' },
    colComp: { flex: '1 1 40%', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e3e5e8', borderRadius: 8, padding: 16, overflow: 'hidden' },
    colSettings: { flex: '0 0 33%', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e3e5e8', borderRadius: 8, padding: 16, overflow: 'auto' },
    colTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
    colHint: { fontSize: 12, color: '#888', marginBottom: 12 },
    search: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginBottom: 12, boxSizing: 'border-box' },
    scroll: { overflowY: 'auto', flex: 1 },
    libCard: { display: 'flex', alignItems: 'stretch', gap: 12, padding: 0, paddingRight: 10, border: '1px solid #eee', borderRadius: 6, marginBottom: 8, overflow: 'hidden' },
    sprite: (w, h) => ({ width: w, height: h, objectFit: 'cover', objectPosition: 'left', background: '#000', borderRadius: 4, flex: 'none' }),
    // cropped footage thumbnail (shows only the footage 66.4%, not the up-next panel)
    thumbWrap: { position: 'relative', width: 104, alignSelf: 'stretch', flex: 'none', overflow: 'hidden', background: '#000' },
    thumbWrapSm: { position: 'relative', width: 104, alignSelf: 'stretch', flex: 'none', overflow: 'hidden', background: '#000' },
    thumbImg: { position: 'absolute', top: 0, left: 0, height: '100%', width: '602.41%', objectFit: 'cover', objectPosition: 'left top' },
    clipName: { fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1a1a2e' },
    clipMeta: { display: 'flex', alignItems: 'center', marginTop: 2 },
    rxText: { fontSize: 11, fontWeight: 600, color: '#5b6169' },
    clipDur: { fontSize: 12, fontWeight: 600, color: '#5b6169', flex: 'none', alignSelf: 'center', minWidth: 44, textAlign: 'right', marginLeft: 'auto', paddingRight: 4 },
    catPill: { alignSelf: 'flex-start', marginTop: 5, borderRadius: 5, padding: '1px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
    addBtn: { alignSelf: 'center', width: 32, height: 32, flex: 'none', borderRadius: 6, border: '1px solid #d7dbe0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a1a2e' },
    totals: { display: 'flex', gap: 24, padding: '12px 16px', background: '#f7f8fa', border: '1px solid #eee', borderRadius: 8, marginBottom: 12 },
    totLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
    totVal: { fontSize: 20, fontWeight: 700 },
    entryCard: { display: 'flex', alignItems: 'stretch', gap: 10, padding: 0, paddingRight: 10, border: '1px solid #eee', borderRadius: 6, marginBottom: 8, overflow: 'hidden' },
    orderBadge: { width: 22, textAlign: 'center', fontWeight: 700, color: '#555', flex: 'none', alignSelf: 'center', paddingLeft: 10 },
    reorderBtns: { display: 'flex', flexDirection: 'column', gap: 2, alignSelf: 'center', flex: 'none' },
    miniBtn: { width: 24, height: 20, border: '1px solid #e3e5e8', borderRadius: 4, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5b6169', padding: 0 },
    miniBtnDisabled: { color: '#d0d4d9', cursor: 'default', background: '#fafafa' },
    removeBtn: { border: 'none', background: 'none', color: '#9aa0a8', cursor: 'pointer', alignSelf: 'center', flex: 'none', display: 'flex', alignItems: 'center', padding: 4, transition: 'color .12s' },
    empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#888', padding: 24 },
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' },
    inactive: { border: '1px dashed #d0d0d0', borderRadius: 8, padding: 14, marginBottom: 16, background: '#fafafa', color: '#999' },
    inactiveTag: { display: 'inline-block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#aaa', border: '1px solid #ddd', borderRadius: 10, padding: '1px 8px', marginLeft: 8 },
    summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, borderBottom: '1px solid #f0f0f0' },
    dirtyBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 24px', background: '#fff9e6', borderBottom: '1px solid #f0e4b8', fontSize: 13, color: '#8a6d00' },
    savedFlash: { color: '#1a7f37', fontWeight: 700, fontSize: 14, marginRight: 6, alignSelf: 'center' },
  };

  // a program-like object for the shared editors (they read these fields)
  const builderProgram = { id: programId || '__new__', tags, coverImageUrl, coverZoom, coverPosX, coverPosY };
  // folder dropdown options: top-level + children (2-level), indented labels
  const topFolders = folders.filter(f => f.parentFolderId == null);
  const folderOptions = [];
  topFolders.forEach(f => {
    folderOptions.push({ id: f.id, label: f.name });
    folders.filter(c => c.parentFolderId === f.id).forEach(c =>
      folderOptions.push({ id: c.id, label: `\u00A0\u00A0\u2514 ${c.name}` }));
  });
  const Inactive = ({ title }) => (
    <div style={S.inactive}>
      <div style={{ fontWeight: 600, color: '#777' }}>
        {title}<span style={S.inactiveTag}>coming soon</span>
      </div>
    </div>
  );

  // Edit-mode gates: show load / not-found before the builder UI.
  if (isEditMode && loadingProgram) {
    return <div style={{ padding: 40, fontFamily: 'system-ui' }}>Loading program…</div>;
  }
  if (isEditMode && loadError) {
    return (
      <div style={{ padding: 40, fontFamily: 'system-ui' }}>
        <p style={{ color: '#b00' }}>{loadError}</p>
        <button style={{ padding: '8px 16px', cursor: 'pointer' }} onClick={() => navigate('/programs')}>
          Back to Programs
        </button>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* HEADER */}
      <div style={S.header}>
        <div>
          <div style={S.headerTitle}>{isEditMode ? 'Edit Program' : 'Program Builder'}</div>
          <div style={S.headerSub}>Build a workout by adding program clips, reordering, and saving.</div>
        </div>
        <div style={S.headerBtns}>
          {savedFlash && <span style={S.savedFlash}>✓ Saved</span>}
          <button style={S.btn} onClick={() => navigate('/programs')}>
            {isEditMode ? 'Done' : 'Cancel'}
          </button>
          <button
            style={canSave ? S.btnPrimary : S.btnDisabled}
            disabled={!canSave}
            onClick={save}
            title={canSave ? 'Save this program' : 'Add a name and at least one clip to save'}
          >
            {saving ? 'Saving…' : 'Save Program'}
          </button>
        </div>
      </div>

      {saveError && (
        <div style={{ padding: '8px 24px', background: '#fdeeee', color: '#b00', borderBottom: '1px solid #f0c9c9', fontSize: 13 }}>
          {saveError}
        </div>
      )}

      {/* DIRTY INDICATOR — at TOP so it's always visible (honest; Save still disabled) */}
      {dirty && (
        <div style={S.dirtyBar}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e0a800', display: 'inline-block' }} />
          <strong>Unsaved changes</strong>
          <span style={{ color: '#8a6d00' }}>Click Save Program to keep your changes. Unsaved changes are lost if you leave or refresh.</span>
        </div>
      )}

      {/* COLUMNS */}
      <div style={S.columns}>
        {/* ---------- LIBRARY ---------- */}
        <div style={S.colLibrary}>
          <div style={S.colTitle}>1 · Program Clip Library</div>
          <div style={S.colHint}>Add any clip. Add it multiple times for circuits.</div>
          <input
            style={S.search}
            placeholder="Search exercises…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={S.scroll}>
            {loading && <p style={{ color: '#888' }}>Loading clips…</p>}
            {!loading && filteredClips.length === 0 && (
              <p style={{ color: '#888' }}>{search ? 'No clips match your search.' : 'No program clips found.'}</p>
            )}
            {filteredClips.map((asset) => {
              const cfg = asset.exerciseConfig || {};
              const rx = prescriptionText(cfg);
              const cat = (asset.categories && asset.categories[0]) || null;
              return (
              <div key={asset.id} style={S.libCard}>
                <div style={S.thumbWrap}><img src={asset.spriteUrl} alt="" style={S.thumbImg} /></div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '6px 0' }}>
                  <div style={S.clipName}>{titleCase(cfg.exerciseName) || '(unnamed)'}</div>
                  <div style={S.clipMeta}>
                    {rx && <span style={S.rxText}>{rx}</span>}
                    <span style={S.clipDur}>{mmss(asset.durationMs)}</span>
                  </div>
                  {cat && <span style={{ ...S.catPill, background: catColor(cat) + '22', color: catColor(cat) }}>{cat}</span>}
                </div>
                <button style={S.addBtn} title="Add to program" onClick={() => addClip(asset)}><Plus size={16} /></button>
              </div>
              );
            })}
          </div>
        </div>

        {/* ---------- COMPOSITION ---------- */}
        <div style={S.colComp}>
          <div style={S.colTitle}>2 · Program Composition</div>
          <div style={S.colHint}>Reorder with the arrows. Remove to delete an occurrence.</div>

          <div style={S.totals}>
            <div>
              <div style={S.totLabel}>Total Duration</div>
              <div style={S.totVal}>{mmss(totalDurationMs)}</div>
            </div>
            <div>
              <div style={S.totLabel}>Program Clips</div>
              <div style={S.totVal}>{clipCount}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>occurrence{clipCount === 1 ? '' : 's'}</div>
            </div>
          </div>

          {addError && (
            <div style={{ background: '#fdeeee', color: '#b00', border: '1px solid #f0c9c9', borderRadius: 6, padding: 8, marginBottom: 8, fontSize: 12, fontFamily: 'monospace' }}>
              {addError}
            </div>
          )}

          <div style={S.scroll}>
            {composition.length === 0 ? (
              <div style={S.empty}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#555', marginBottom: 6 }}>Build your program</div>
                <div>Add a program clip from the library to get started.</div>
                <div style={{ marginTop: 4 }}>You can add the same clip more than once.</div>
              </div>
            ) : (
              composition.map((e, i) => {
                // display-only lookup: the ProgramEntry stays the locked 7 fields;
                // category + prescription come from the source asset for display.
                const src = programClips.find((a) => a.id === e.assetId);
                const cfg = src?.exerciseConfig || {};
                const rx = prescriptionText(cfg);
                const cat = (src?.categories && src.categories[0]) || null;
                return (
                <div key={e.entryId} style={S.entryCard}>
                  <div style={S.orderBadge}>{e.order + 1}</div>
                  <div style={S.thumbWrapSm}><img src={e.spriteUrl} alt="" style={S.thumbImg} /></div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '6px 0' }}>
                    <div style={S.clipName}>{titleCase(e.exerciseName)}</div>
                    <div style={S.clipMeta}>
                      {rx && <span style={S.rxText}>{rx}</span>}
                      <span style={S.clipDur}>{mmss(e.durationMs)}</span>
                    </div>
                    {cat && <span style={{ ...S.catPill, background: catColor(cat) + '22', color: catColor(cat) }}>{cat}</span>}
                  </div>
                  <div style={S.reorderBtns}>
                    <button style={{ ...S.miniBtn, ...(i === 0 ? S.miniBtnDisabled : {}) }} onClick={() => moveEntry(i, -1)} disabled={i === 0} title="Move up"><ChevronUp size={15} /></button>
                    <button style={{ ...S.miniBtn, ...(i === composition.length - 1 ? S.miniBtnDisabled : {}) }} onClick={() => moveEntry(i, +1)} disabled={i === composition.length - 1} title="Move down"><ChevronDown size={15} /></button>
                  </div>
                  <button style={S.removeBtn} onClick={() => removeEntry(e.entryId)} title="Remove occurrence" onMouseEnter={(ev) => ev.currentTarget.style.color = '#e03a3a'} onMouseLeave={(ev) => ev.currentTarget.style.color = '#9aa0a8'}><Trash2 size={16} /></button>
                </div>
                );
              })
            )}
          </div>
        </div>

        {/* ---------- SETTINGS ---------- */}
        <div style={S.colSettings}>
          <div style={S.colTitle}>3 · Program Settings</div>
          <div style={S.colHint}>Name it, organize, and save.</div>

          {/* Cover image (top) — reuses shared CoverImageEditor; staged locally */}
          <div style={S.field}>
            <label style={S.label}>Cover Image</label>
            <div
              style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: coverImageUrl ? '#1a1d21' : '#f1f3f5', border: '1px solid #e3e5e8', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setCoverModalOpen(true)}
            >
              {coverImageUrl ? (
                <img src={coverImageUrl} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${coverPosX}% ${coverPosY}%`, transform: `scale(${coverZoom})`, transformOrigin: `${coverPosX}% ${coverPosY}%` }} />
              ) : (
                <span style={{ color: '#9aa0a8', fontSize: 13 }}>+ Add cover</span>
              )}
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Program Name</label>
            <input
              style={S.input}
              placeholder="e.g. Arm Care — Week 1"
              value={programName}
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>

          {/* Accent + Player preview remain COMING SOON (own future piece) */}
          {/* Accent Color — swatch row (writes theme.accent; text/muted stay neutral) */}
          <div style={S.field}>
            <label style={S.label}>Accent Color <span style={{ color: '#9aa0a8', fontWeight: 400 }}>(changes player UI)</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {Array.from(new Set([DEFAULT_THEME.accent, ...accentPalette])).slice(0, 7).map(hex => {
                const selected = (theme.accent || '').toLowerCase() === hex.toLowerCase();
                return (
                  <div key={hex} onClick={() => selectAccent(hex)} title={hex}
                    style={{ width: 26, height: 26, borderRadius: '50%', background: hex, cursor: 'pointer', flexShrink: 0,
                      border: selected ? '2px solid #E63946' : '1px solid #e0e0e0',
                      boxShadow: selected ? '0 0 0 2px rgba(230,57,70,0.2)' : 'none' }} />
                );
              })}
              <div onClick={() => setAccentModalOpen(true)} title="Add a custom color"
                style={{ width: 26, height: 26, borderRadius: '50%', border: '1px dashed #c7ccd1', color: '#9aa0a8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>+</div>
            </div>
          </div>
          {/* Player Accent Preview — mirrors the real player; accent elements re-color live */}
          <div style={S.field}>
            <label style={S.label}>Player Accent Preview</label>
            <PlayerAccentPreview accent={theme.accent || DEFAULT_THEME.accent} composition={composition} />
            <div style={{ fontSize: 11, color: '#9aa0a8', marginTop: 6, lineHeight: 1.4 }}>
              This preview shows the live UI that uses your accent color. Clip footage (name/prescription)
              is baked into the video and does not change here.
            </div>
          </div>

          {/* Folder — real dropdown from the user's folders (2-level) */}
          <div style={S.field}>
            <label style={S.label}>Folder</label>
            <select style={S.input} value={folderId || ''} onChange={(e) => onFolderChange(e.target.value)}>
              <option value="">Unfiled</option>
              {folderOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>

          {/* Tags — colored chips + Edit (reuses shared TagEditor) */}
          <div style={S.field}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={S.label}>Tags</label>
              <span style={{ color: '#E63946', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => setTagsModalOpen(true)}>Edit</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {tags.length === 0 && <span style={{ fontSize: 13, color: '#9aa0a8' }}>No tags</span>}
              {tags.map(t => (<span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '3px 8px', fontSize: 12, fontWeight: 600, background: tagBg(t), color: tagColor(t) }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: tagColor(t) }} />{t}</span>))}
            </div>
          </div>

          {/* REAL summary — same derived values as the center header */}
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Summary</div>
            <div style={S.summaryRow}><span>Program Clips</span><strong>{clipCount}</strong></div>
            <div style={S.summaryRow}><span>Total Duration</span><strong>{mmss(totalDurationMs)}</strong></div>
          </div>
        </div>
      </div>

      {coverModalOpen && (
        <CoverImageEditor
          program={builderProgram}
          onClose={() => setCoverModalOpen(false)}
          onSave={async (_id, c) => { onCoverChange(c); }}
        />
      )}
      {accentModalOpen && (
        <AccentColorModal
          initial={theme.accent || DEFAULT_THEME.accent}
          recent={accentPalette}
          onClose={() => setAccentModalOpen(false)}
          onSave={saveCustomAccent}
        />
      )}
      {tagsModalOpen && (
        <TagEditor
          program={builderProgram}
          allTags={tagPool}
          onClose={() => setTagsModalOpen(false)}
          onSave={async (_id, next) => { onTagsChange(next); }}
        />
      )}

    </div>
  );
}



// ---- Player Accent Preview — faithful mini-render of the WebView player ----
// Left 66.4% = first clip's real sprite (baked colors, unchanged). Right 33.6% = up-next
// panel. Coachbar (top) + bottombar (bottom). All var(--accent) elements driven by `accent`.
function PlayerAccentPreview({ accent, composition }) {
  const first = composition[0] || null;
  const next = composition[1] || composition[0] || null;   // up-next = next clip (or loop)
  const spriteCrop = { position: 'absolute', top: 0, left: 0, height: '100%', width: '602.41%', objectFit: 'cover', objectPosition: 'left top' };
  return (
    <div style={{ ...PP.player, '--accent': accent }}>
      {/* coachbar */}
      <div style={PP.coachbar}>
        <span style={{ ...PP.coachBtn, borderColor: accent }}>Pause</span>
        <span style={PP.coachBtnPlain}>Restart</span>
        <div style={PP.scrubTrack}><div style={{ ...PP.scrubFill, background: accent }} /></div>
        <span style={PP.clock}>0:09 / 12:50</span>
      </div>
      {/* stage */}
      <div style={PP.stage}>
        <div style={PP.footage}>
          {first?.spriteUrl
            ? <img src={first.spriteUrl} alt="" style={spriteCrop} />
            : <div style={PP.footEmpty}>Add a clip to preview</div>}
        </div>
        <div style={PP.upnext}>
          <div style={{ ...PP.unLabel, color: accent }}>» Up Next</div>
          <div style={PP.unName}>{next?.exerciseName || '—'}</div>
          <div style={{ ...PP.unThumb, borderColor: accent }}>
            {next?.spriteUrl && <img src={next.spriteUrl} alt="" style={spriteCrop} />}
          </div>
        </div>
      </div>
      {/* bottombar */}
      <div style={{ ...PP.bottombar, borderTop: `2px solid ${accent}` }}>
        <span style={PP.bbEx}>Exercise<span style={PP.bbCnt}> 1 / {composition.length || 1}</span></span>
        <div style={PP.bbTrack}><div style={{ ...PP.bbFill, background: accent }} /></div>
        <div style={PP.bbRight}>
          <span style={PP.bbTl}>Time Left</span><span style={PP.bbTv}>3:21</span>
          <span style={PP.bbTl}>Workout Left</span><span style={PP.bbTv}>12:41</span>
        </div>
      </div>
    </div>
  );
}

const PP = {
  player: { background: '#14181d', borderRadius: 6, overflow: 'hidden', marginTop: 6 },
  coachbar: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: '#0d1015' },
  coachBtn: { background: '#232a32', color: '#fff', border: '2px solid', borderRadius: 5, fontSize: 9, fontWeight: 600, padding: '2px 8px', whiteSpace: 'nowrap' },
  coachBtnPlain: { background: '#232a32', color: '#cfd6de', border: '2px solid #2f3945', borderRadius: 5, fontSize: 9, fontWeight: 600, padding: '2px 8px', whiteSpace: 'nowrap' },
  scrubTrack: { flex: 1, height: 5, background: 'rgba(255,255,255,.15)', borderRadius: 5, overflow: 'hidden' },
  scrubFill: { height: '100%', width: '12%', borderRadius: 5 },
  clock: { color: '#8a8f98', fontSize: 9, whiteSpace: 'nowrap' },
  stage: { position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', overflow: 'hidden' },
  footage: { position: 'absolute', top: 0, left: 0, bottom: 0, width: '66.4%', overflow: 'hidden', background: '#1a1d21' },
  footEmpty: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5b6169', fontSize: 11 },
  upnext: { position: 'absolute', top: 0, right: 0, bottom: 0, width: '33.6%', padding: '3% 2.4%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '5%', background: '#000' },
  unLabel: { fontWeight: 700, letterSpacing: '.08em', fontSize: 11, textTransform: 'uppercase' },
  unName: { color: '#fff', fontWeight: 700, textTransform: 'uppercase', fontSize: 9, opacity: .95, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unThumb: { position: 'relative', width: '72%', aspectRatio: '10.624 / 9', overflow: 'hidden', borderRadius: 3, background: '#111', border: '2px solid' },
  bottombar: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: '#000' },
  bbEx: { color: '#8a8f98', fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  bbCnt: { color: '#fff', fontWeight: 700 },
  bbTrack: { flex: 1, height: 5, background: 'rgba(255,255,255,.15)', borderRadius: 5, overflow: 'hidden' },
  bbFill: { height: '100%', width: '42%', borderRadius: 4 },
  bbRight: { display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' },
  bbTl: { color: '#8a8f98', fontSize: 7, fontWeight: 700, textTransform: 'uppercase' },
  bbTv: { color: '#fff', fontSize: 10, fontWeight: 700 },
};

// ---- Accent color modal (mirrors the Video Builder brand-colors picker) ----
function AccentColorModal({ initial, recent, onClose, onSave }) {
  const [draft, setDraft] = useState(initial || '#c8e020');
  const valid = /^#[0-9a-fA-F]{6}$/.test(draft);
  return (
    <div style={AM.overlay} onClick={onClose}>
      <div style={AM.modal} onClick={e => e.stopPropagation()}>
        <div style={AM.header}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e' }}>Accent Color</div>
          <button onClick={onClose} style={AM.close}>×</button>
        </div>
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <HexColorPicker color={draft} onChange={setDraft} style={{ width: 180, height: 140 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ width: '100%', height: 48, borderRadius: 6, background: valid ? draft : '#eee', border: '1px solid #e0e0e0' }} />
              <HexColorInput color={draft} onChange={setDraft} prefixed
                style={{ width: '100%', fontSize: 13, fontFamily: 'monospace', padding: '6px 8px',
                  border: '1px solid #e0e0e0', borderRadius: 6, background: '#fafafa', color: '#1a1a2e', boxSizing: 'border-box', outline: 'none' }} />
              {recent && recent.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Saved</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {recent.map((c, i) => (
                      <div key={i} onClick={() => setDraft(c)} title={c}
                        style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', flexShrink: 0,
                          border: draft.toLowerCase() === c.toLowerCase() ? '2px solid #E63946' : '1px solid #e0e0e0' }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={AM.footer}>
          <button onClick={onClose} style={AM.btnSecondary}>Cancel</button>
          <button onClick={() => valid && onSave(draft)} disabled={!valid}
            style={{ ...AM.btnPrimary, opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'default' }}>Save Color</button>
        </div>
      </div>
    </div>
  );
}

const AM = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 10, width: 460, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' },
  close: { border: 'none', background: 'none', fontSize: 22, color: '#9aa0a8', cursor: 'pointer', lineHeight: 1 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px 16px', borderTop: '1px solid #f0f1f4' },
  btnSecondary: { padding: '8px 16px', border: '1px solid #e0e0e0', background: '#fff', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', border: 'none', background: '#E63946', color: '#fff', borderRadius: 6, fontSize: 14, fontWeight: 600 },
};